/**
 * LearningProfileTab
 * 
 * Catalyst-only tab for managing technician learning preferences.
 */

import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  User, 
  Save, 
  Loader2, 
  Sparkles,
  BookOpen,
  Headphones,
  Eye,
  Wrench,
  GraduationCap
} from 'lucide-react';
import { useLearningProfile, useUpdateLearningProfile } from '@/hooks/useLearningProfile';
import { 
  LEARNING_STYLE_LABELS, 
  LEARNING_CONSTRAINT_LABELS,
  SKILL_DEPTH_LABELS,
  type LearningStyle, 
  type LearningConstraint,
  type SkillDepth
} from '@/types/training';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const STYLE_ICONS: Record<LearningStyle, React.ReactNode> = {
  visual: <Eye className="h-4 w-4" />,
  auditory: <Headphones className="h-4 w-4" />,
  read_write: <BookOpen className="h-4 w-4" />,
  hands_on: <Wrench className="h-4 w-4" />,
};

interface LearningProfileTabProps {
  technicianProfileId: string;
  proficiencies?: Array<{ category_key: string }>;
}

export function LearningProfileTab({ 
  technicianProfileId,
  proficiencies = []
}: LearningProfileTabProps) {
  const { data: profile, isLoading } = useLearningProfile(technicianProfileId);
  const updateProfile = useUpdateLearningProfile();
  
  // Local state for form
  const [selectedStyles, setSelectedStyles] = useState<LearningStyle[]>(
    profile?.preferred_styles || []
  );
  const [selectedConstraints, setSelectedConstraints] = useState<LearningConstraint[]>(
    profile?.constraints || []
  );
  const [currentLevel, setCurrentLevel] = useState<SkillDepth>(
    profile?.current_level || 'foundation'
  );
  const [focusDomains, setFocusDomains] = useState<string[]>(
    profile?.focus_domains || []
  );
  
  // Update local state when profile loads
  useEffect(() => {
    if (profile) {
      setSelectedStyles(profile.preferred_styles || []);
      setSelectedConstraints(profile.constraints || []);
      setCurrentLevel(profile.current_level || 'foundation');
      setFocusDomains(profile.focus_domains || []);
    }
  }, [profile]);

  const handleStyleToggle = (style: LearningStyle) => {
    setSelectedStyles(prev => 
      prev.includes(style) 
        ? prev.filter(s => s !== style)
        : [...prev, style]
    );
  };

  const handleConstraintToggle = (constraint: LearningConstraint) => {
    setSelectedConstraints(prev => 
      prev.includes(constraint) 
        ? prev.filter(c => c !== constraint)
        : [...prev, constraint]
    );
  };

  const handleDomainToggle = (domain: string) => {
    setFocusDomains(prev => 
      prev.includes(domain) 
        ? prev.filter(d => d !== domain)
        : [...prev, domain]
    );
  };

  const handleSave = async () => {
    try {
      await updateProfile.mutateAsync({
        technicianProfileId,
        preferredStyles: selectedStyles,
        constraints: selectedConstraints,
        currentLevel,
        focusDomains,
      });
      toast.success('Learning profile saved');
    } catch (error) {
      toast.error('Failed to save learning profile');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const availableDomains = proficiencies.map(p => p.category_key);

  return (
    <div className="space-y-6">
      {/* Inferred Preferences Summary */}
      {profile?.inferred_styles && Object.keys(profile.inferred_styles).length > 0 && (
        <Card className="border-dashed">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-primary" />
              Inferred from Your Activity
            </CardTitle>
            <CardDescription className="text-xs">
              Based on what training you've engaged with most
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {Object.entries(profile.inferred_styles)
                .sort(([, a], [, b]) => (b as number) - (a as number))
                .slice(0, 3)
                .map(([style, weight]) => (
                  <Badge key={style} variant="secondary" className="text-xs">
                    {STYLE_ICONS[style as LearningStyle]}
                    <span className="ml-1">
                      {LEARNING_STYLE_LABELS[style as LearningStyle]}
                    </span>
                    <span className="ml-1 text-muted-foreground">
                      ({Math.round((weight as number) * 100)}%)
                    </span>
                  </Badge>
                ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Preferred Learning Styles */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <GraduationCap className="h-4 w-4" />
            Preferred Learning Styles
          </CardTitle>
          <CardDescription className="text-xs">
            Select how you learn best
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-3">
            {(Object.entries(LEARNING_STYLE_LABELS) as [LearningStyle, string][]).map(([style, label]) => (
              <div 
                key={style}
                className="flex items-center space-x-2"
              >
                <Checkbox
                  id={`style-${style}`}
                  checked={selectedStyles.includes(style)}
                  onCheckedChange={() => handleStyleToggle(style)}
                />
                <Label 
                  htmlFor={`style-${style}`}
                  className="text-sm flex items-center gap-2 cursor-pointer"
                >
                  {STYLE_ICONS[style]}
                  {label}
                </Label>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Learning Constraints */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <User className="h-4 w-4" />
            Learning Constraints
          </CardTitle>
          <CardDescription className="text-xs">
            Tell us what works for your schedule
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-3">
            {(Object.entries(LEARNING_CONSTRAINT_LABELS) as [LearningConstraint, string][]).map(([constraint, label]) => (
              <div 
                key={constraint}
                className="flex items-center space-x-2"
              >
                <Checkbox
                  id={`constraint-${constraint}`}
                  checked={selectedConstraints.includes(constraint)}
                  onCheckedChange={() => handleConstraintToggle(constraint)}
                />
                <Label 
                  htmlFor={`constraint-${constraint}`}
                  className="text-sm cursor-pointer"
                >
                  {label}
                </Label>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Current Skill Level */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">Current Skill Level</CardTitle>
          <CardDescription className="text-xs">
            This helps match training to your experience
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Select value={currentLevel} onValueChange={(v) => setCurrentLevel(v as SkillDepth)}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Select level" />
            </SelectTrigger>
            <SelectContent>
              {(Object.entries(SKILL_DEPTH_LABELS) as [SkillDepth, string][]).map(([level, label]) => (
                <SelectItem key={level} value={level}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Focus Domains */}
      {availableDomains.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Focus Domains</CardTitle>
            <CardDescription className="text-xs">
              Prioritize training in these areas
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {availableDomains.map((domain) => (
                <Badge
                  key={domain}
                  variant={focusDomains.includes(domain) ? 'default' : 'outline'}
                  className="cursor-pointer"
                  onClick={() => handleDomainToggle(domain)}
                >
                  {domain.replace(/_/g, ' ')}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Save Button */}
      <Button 
        onClick={handleSave} 
        disabled={updateProfile.isPending}
        className="w-full"
      >
        {updateProfile.isPending ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Saving...
          </>
        ) : (
          <>
            <Save className="h-4 w-4 mr-2" />
            Save Preferences
          </>
        )}
      </Button>
    </div>
  );
}
